import React from 'react';
import PropTypes from 'prop-types';
import { LineItem } from 'common/display/LineItem';
import ShortDates from 'common/utils/Dates/ShortDates';
import formatSpend from 'common/utils/Money/formatSpend';
import ValueWithStrikethroughChanges from './ValueWithStrikethroughChanges';

const BaseLineItem = ({ ...props }) => <LineItem valueClassName="m-0" {...props} />;

const AmountLineItem = ({
  currentAmount,
  fieldName,
  isUnitBased,
  previousAmount,
  showOriginalValues,
  unit,
}) => {
  const displayedAmount = formatSpend(currentAmount, unit, !isUnitBased);
  const displayedPreviousAmount = previousAmount && formatSpend(previousAmount, unit, !isUnitBased);

  return (
    <BaseLineItem field={fieldName} dataTestElement="amount-requested">
      <ValueWithStrikethroughChanges
        currentValue={displayedAmount}
        previousValue={displayedPreviousAmount}
        showOriginalValues={showOriginalValues}
      />
    </BaseLineItem>
  );
};

AmountLineItem.propTypes = {
  currentAmount: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  isUnitBased: PropTypes.bool.isRequired,
  previousAmount: PropTypes.number,
  showOriginalValues: PropTypes.bool,
  unit: PropTypes.string,
};

AmountLineItem.defaultProps = {
  previousAmount: null,
  showOriginalValues: false,
  unit: null,
};

const ServiceDeliveryDatesLineItem = ({
  endDate,
  fieldName,
  previousEndDate,
  previousStartDate,
  showOriginalValues,
  startDate,
  testElement,
}) => {
  const currentValue = <ShortDates startDate={startDate} endDate={endDate} />;
  const previousValue = previousEndDate ?
    <ShortDates startDate={previousStartDate} endDate={previousEndDate} /> :
    null;

  return (
    <BaseLineItem field={fieldName} dataTestElement={testElement}>
      <ValueWithStrikethroughChanges
        currentValue={currentValue}
        previousValue={previousValue}
        showOriginalValues={showOriginalValues}
      />
    </BaseLineItem>
  );
};

ServiceDeliveryDatesLineItem.propTypes = {
  endDate: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  previousEndDate: PropTypes.string,
  previousStartDate: PropTypes.string,
  showOriginalValues: PropTypes.bool,
  startDate: PropTypes.string.isRequired,
  testElement: PropTypes.string,
};

ServiceDeliveryDatesLineItem.defaultProps = {
  previousEndDate: null,
  previousStartDate: null,
  showOriginalValues: false,
  testElement: 'delivery-dates-line-item',
};

const ClinicalModificationsLineItem = ({
  clinicalModifications,
  previousClinicalModifications,
  showOriginalValues,
}) => {
  const displayedClinicalModifications = clinicalModifications?.map((cm) => cm.code)?.join(',');
  const displayedPreviousClinicalModifications = previousClinicalModifications?.length > 0 ?
    previousClinicalModifications.map((cm) => cm.code).join(',') :
    null;

  return (
    <BaseLineItem field="ICD-10 Code">
      <ValueWithStrikethroughChanges
        currentValue={displayedClinicalModifications}
        previousValue={displayedPreviousClinicalModifications}
        showOriginalValues={showOriginalValues}
      />
    </BaseLineItem>
  );
};

const clinicalModificationPropType = PropTypes.shape({
  code: PropTypes.string.isRequired,
});

ClinicalModificationsLineItem.propTypes = {
  clinicalModifications: PropTypes.arrayOf(clinicalModificationPropType).isRequired,
  previousClinicalModifications: PropTypes.arrayOf(clinicalModificationPropType),
  showOriginalValues: PropTypes.bool,
};

ClinicalModificationsLineItem.defaultProps = {
  previousClinicalModifications: [],
  showOriginalValues: false,
};

const CapInformationLineItem = ({ capInformation }) => (
  <BaseLineItem field="Program Cap">
    {capInformation ? (
      /* eslint-disable-next-line react/no-danger */
      <div dangerouslySetInnerHTML={{ __html: capInformation }} />
    ) : (
      <span>No cap</span>
    )}
  </BaseLineItem>
);

CapInformationLineItem.propTypes = {
  capInformation: PropTypes.string,
};

CapInformationLineItem.defaultProps = {
  capInformation: '',
};

const NoteLineItem = ({ children, fieldName }) => (
  <BaseLineItem field={fieldName}>
    <div className="whitespace-pre-line">{children}</div>
  </BaseLineItem>
);

NoteLineItem.propTypes = {
  children: PropTypes.node.isRequired,
  fieldName: PropTypes.string,
};

NoteLineItem.defaultProps = {
  fieldName: 'Note',
};

export {
  AmountLineItem,
  BaseLineItem,
  CapInformationLineItem,
  ClinicalModificationsLineItem,
  NoteLineItem,
  ServiceDeliveryDatesLineItem,
};
